import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  ChevronLeft, BookOpen, CheckCircle2, Clock, FileText,
  PlayCircle, Award, AlertCircle, Loader2, Video, Link as LinkIcon,
} from 'lucide-react';
import { BACKEND_URL } from '../../config/canonical';

const apiCall = async (path, opts = {}) => {
  const token = localStorage.getItem('nexus_token');
  const res = await fetch(`${BACKEND_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
    ...opts,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
  return data;
};

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [kcAnswers, setKcAnswers] = useState({});
  const [kcResult, setKcResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await apiCall(`/api/training-center/courses/${courseId}`);
      setCourse(r.course);
      setProgress(r.progress);
      if (!activeLessonId && r.course.lessons?.length) {
        const nextIdx = r.course.lessons.findIndex(l => !r.progress.completed_lessons.includes(l.id));
        setActiveLessonId(r.course.lessons[Math.max(0, nextIdx)].id);
      }
    } catch (e) { toast.error(e.message); }
  }, [courseId, activeLessonId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Auto-call /start so progress flips to IN_PROGRESS
    if (courseId) {
      apiCall(`/api/training-center/courses/${courseId}/start`, { method: 'POST' }).catch(() => {});
    }
  }, [courseId]);

  const activeLesson = useMemo(() =>
    course?.lessons?.find(l => l.id === activeLessonId) || null,
    [course, activeLessonId]);

  const completedLessons = progress?.completed_lessons || [];
  const allLessonsDone = course && completedLessons.length >= course.lessons.length;

  const submitKnowledgeCheck = async () => {
    if (!activeLesson) return;
    const answers = (activeLesson.knowledge_check || []).map((_, i) => kcAnswers[i] ?? -1);
    setSubmitting(true);
    try {
      const r = await apiCall(`/api/training-center/courses/${courseId}/lessons/${activeLesson.id}/complete`, {
        method: 'POST',
        body: JSON.stringify({ lesson_id: activeLesson.id, answers }),
      });
      setKcResult(r.knowledge_check);
      setProgress(r.progress);
      toast.success(r.knowledge_check ? `Lesson complete · ${r.knowledge_check.score_pct}%` : 'Lesson complete');
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  const goNextLesson = () => {
    if (!course) return;
    const idx = course.lessons.findIndex(l => l.id === activeLessonId);
    if (idx >= 0 && idx < course.lessons.length - 1) {
      setActiveLessonId(course.lessons[idx + 1].id);
      setKcAnswers({}); setKcResult(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goPrevLesson = () => {
    if (!course) return;
    const idx = course.lessons.findIndex(l => l.id === activeLessonId);
    if (idx > 0) {
      setActiveLessonId(course.lessons[idx - 1].id);
      setKcAnswers({}); setKcResult(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const activeIdx = course?.lessons?.findIndex(l => l.id === activeLessonId) ?? -1;
  const hasPrev = activeIdx > 0;
  const hasNext = activeIdx >= 0 && activeIdx < (course?.lessons?.length ?? 0) - 1;

  const startExam = () => navigate(`/training-center/course/${courseId}/exam`);

  if (!course) return <div className="min-h-screen bg-[#0A0F1A] text-white p-10 font-mono text-sm">Loading course…</div>;

  return (
    <div className="min-h-screen bg-[#0A0F1A] text-white" data-testid="course-detail-page">
      <div className="border-b border-[#1E293B] bg-[#0F1B33]/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex items-center gap-4 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => navigate('/training-center')} className="border-slate-600 !text-slate-200 hover:bg-slate-800" data-testid="back-to-trc">
            <ChevronLeft className="w-4 h-4 mr-1" /> Training Center
          </Button>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-300">{course.category.replace(/_/g, ' ')}</p>
            <h1 className="text-xl sm:text-2xl font-bold text-white">{course.code} · {course.title}</h1>
            <p className="text-xs text-slate-400 mt-0.5">{course.summary}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-mono uppercase text-slate-400">Progress</p>
            <p className="text-2xl font-bold text-amber-300" data-testid="cd-pct">{progress?.completed_pct ?? 0}%</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Lesson sidebar */}
        <div className="lg:col-span-3">
          <Card className="bg-[#131A2A] border-[#1E293B] sticky top-4">
            <CardContent className="p-3">
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400 px-2 pb-2">Lessons</p>
              <div className="space-y-1.5" data-testid="cd-lesson-list">
                {course.lessons.map((l, i) => {
                  const done = completedLessons.includes(l.id);
                  const active = activeLessonId === l.id;
                  return (
                    <button key={l.id}
                      onClick={() => { setActiveLessonId(l.id); setKcAnswers({}); setKcResult(null); }}
                      className={`w-full text-left rounded px-2 py-2 text-xs flex items-center gap-2 transition ${
                        active ? 'bg-amber-500/15 border border-amber-500/40 text-white'
                               : 'hover:bg-slate-800/60 border border-transparent text-slate-300'}`}
                      data-testid={`cd-lesson-btn-${l.id}`}>
                      {done ? <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                            : <span className="w-4 h-4 rounded-full border border-slate-500 flex-shrink-0 inline-flex items-center justify-center text-[9px] text-slate-400">{i + 1}</span>}
                      <span className="min-w-0 truncate flex-1">{l.title}</span>
                      <span className="text-[10px] text-slate-500 font-mono">{l.minutes}m</span>
                    </button>
                  );
                })}
              </div>
              <div className="border-t border-slate-700 mt-3 pt-3 px-1">
                <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400 mb-1.5">Final Exam</p>
                <div className="text-[11px] text-slate-400 leading-relaxed">
                  {course.final_exam_size} questions · {course.passing_score}% to pass · {course.final_exam_time_limit_min} min
                </div>
                <Button onClick={startExam} disabled={!allLessonsDone}
                  className={`w-full mt-2 ${allLessonsDone ? 'bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold' : 'bg-slate-800 !text-slate-500 cursor-not-allowed'}`}
                  data-testid="cd-start-exam-btn">
                  <Award className="w-4 h-4 mr-1.5" /> {allLessonsDone ? 'Start final exam' : 'Locked'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Lesson body */}
        <div className="lg:col-span-9">
          {activeLesson && (
            <Card className="bg-[#131A2A] border-[#1E293B]">
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <Badge className="bg-slate-800 border border-slate-600 text-slate-300 text-[10px] font-mono"><Clock className="w-3 h-3 mr-1 inline" /> {activeLesson.minutes} min</Badge>
                  <Badge className="bg-slate-800 border border-slate-600 text-slate-300 text-[10px] font-mono"><BookOpen className="w-3 h-3 mr-1 inline" /> {activeLesson.id}</Badge>
                  {completedLessons.includes(activeLesson.id) && (
                    <Badge className="bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 text-[10px] font-mono uppercase">Completed</Badge>
                  )}
                </div>

                {/* Video block — only renders when admin set a URL for this lesson */}
                {activeLesson.video_url && (
                  <div className="rounded-md border border-slate-700 bg-slate-900/40 p-2 mb-4" data-testid="cd-video-block">
                    <iframe title="lesson-video" src={activeLesson.video_url}
                            className="w-full aspect-video rounded" allowFullScreen />
                  </div>
                )}

                <article className="prose prose-invert prose-slate max-w-none prose-headings:text-white prose-p:text-slate-300 prose-strong:text-amber-200 prose-li:text-slate-300 prose-h1:text-2xl prose-h2:text-lg prose-h3:text-base" data-testid="cd-lesson-body">
                  <ReactMarkdown>{activeLesson.body_md}</ReactMarkdown>
                </article>

                {/* Knowledge check */}
                {activeLesson.knowledge_check?.length > 0 && (
                  <div className="mt-6 border-t border-slate-700 pt-5" data-testid="cd-knowledge-check">
                    <h3 className="text-base font-bold text-white mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-amber-300" /> Knowledge check
                    </h3>
                    <div className="space-y-4">
                      {activeLesson.knowledge_check.map((kc, i) => (
                        <div key={i} className="rounded-md border border-slate-700 bg-slate-900/40 p-4" data-testid={`cd-kc-q-${i}`}>
                          <p className="text-sm text-white font-medium">{i + 1}. {kc.q}</p>
                          <div className="space-y-1.5 mt-3">
                            {kc.options.map((opt, j) => {
                              const picked = kcAnswers[i] === j;
                              const result = kcResult?.results?.[i];
                              const isCorrect = result && result.correct_index === j;
                              const isWrongPick = result && result.picked === j && !result.ok;
                              return (
                                <label key={j} className={`flex items-start gap-2 px-2 py-1.5 rounded text-sm cursor-pointer transition ${
                                  isCorrect ? 'bg-emerald-500/15 border border-emerald-500/40 text-emerald-200'
                                  : isWrongPick ? 'bg-rose-500/15 border border-rose-500/40 text-rose-200'
                                  : picked ? 'bg-amber-500/15 border border-amber-500/40 text-white'
                                  : 'border border-transparent hover:bg-slate-800/60 text-slate-300'
                                }`}>
                                  <input type="radio" name={`kc-${i}`} className="mt-1 accent-amber-400"
                                         checked={picked}
                                         disabled={!!kcResult}
                                         onChange={() => setKcAnswers({ ...kcAnswers, [i]: j })}
                                         data-testid={`cd-kc-${i}-${j}`} />
                                  <span className="min-w-0 flex-1">{opt}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>

                    {!kcResult ? (
                      <Button onClick={submitKnowledgeCheck} disabled={submitting}
                        className="mt-4 bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold"
                        data-testid="cd-submit-kc">
                        {submitting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Submitting…</> : 'Submit & complete lesson'}
                      </Button>
                    ) : (
                      <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
                        <p className="text-sm font-mono">
                          Score: <span className="font-bold text-amber-300">{kcResult.score_pct}%</span> · {kcResult.correct_count} / {kcResult.total}
                        </p>
                        <Button onClick={goNextLesson} className="bg-emerald-500 hover:bg-emerald-400 !text-slate-900 font-semibold" data-testid="cd-next-lesson">
                          Next lesson <PlayCircle className="w-4 h-4 ml-1.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* === Lesson navigation (always visible, independent of KC) === */}
                <div className="mt-6 pt-5 border-t border-slate-700 flex items-center justify-between gap-2 flex-wrap" data-testid="lesson-nav">
                  <Button
                    onClick={goPrevLesson}
                    disabled={!hasPrev}
                    variant="outline"
                    className="border-slate-500 !text-slate-200 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="cd-prev-lesson"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1.5" /> Previous lesson
                  </Button>
                  <p className="text-[11px] text-slate-500 font-mono">
                    Lesson {activeIdx + 1} of {course.lessons.length}
                  </p>
                  <Button
                    onClick={goNextLesson}
                    disabled={!hasNext}
                    className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="cd-next-lesson-bottom"
                  >
                    Next lesson <PlayCircle className="w-4 h-4 ml-1.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseDetail;
